import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { BaseView, BaseSafeFormView, BaseText } from '@/components/base'
import {
  StickyHeaderNavigation,
  SearchInput,
  ContactsList,
  FavoritesList,
  ShareRequest,
  ScrollToTopButton,
  AppButton,
} from '@/components'
import { Avatar } from '@/components/Avatar'
import { ContactItem } from '@/components/ContactItem'
import { useSendStore } from '@/store/useSendStore'
import { useMultipleRecipientsStore } from '@/store/useMultipleRecipientsStore'
import type { UsersAndContacts } from '@/types/contacts'
import { useFeatureFlag, useScrollToTop } from '@/hooks'
import { getAvatarProps } from '@/utils/avatarUtils'
import { useTranslation } from 'react-i18next'
import { useUserStore } from '@/store/useUserStore'
import useOffsets from '@/hooks/useOffsets'
import { useFavoriteUsers } from '@/hooks/useContacts'

interface RecipientParams {
  currencyValue: string
  currencyCode: string
  fiatValue: string
  fiatCode: string
  type: 'send' | 'request'
  id?: string
  avatar?: string
  name?: string
  username?: string
  phoneNumber?: string
  avatarBg?: [string, string, ...string[]]
  byApp?: string
}

// Mock contacts data removed - no longer needed

// Custom ContactItem with increased phone number text size for Contacts section
const _ContactsBlockItem: React.FC<{
  contact: UsersAndContacts
  onPress: (contact: UsersAndContacts) => void
}> = ({ contact, onPress }) => {
  const avatarProps = getAvatarProps({
    first_name: contact.first_name,
    last_name: contact.last_name,
    avatar_path: contact.avatar_path,
    avatarBg: contact.avatarBg,
    id: contact.id,
  })

  const displayName = contact.first_name || contact.username || 'Unknown'
  const subtitle = contact.username
    ? `@${contact.username}`
    : contact.phone_number

  const handlePress = () => {
    onPress(contact)
  }

  return (
    <BaseView
      data-component="contacts-list-item"
      bgColor="background.cardItemRoot"
      row
      align="center"
      gap={12}
      className="mb-2 cursor-pointer hover:opacity-90 active:scale-[0.99] transition-all duration-150 ease-out"
      style={{
        padding: '12px',
        borderRadius: '24px',
        minHeight: '72px',
      }}
      onClick={handlePress}
    >
      {/* Avatar */}
      <Avatar
        size={62}
        initials={avatarProps.initials}
        imageUrl={avatarProps.avatarUrl}
        gradientColors={avatarProps.backgroundGradient}
        fullSize={false}
      />

      {/* Contact Info */}
      <BaseView
        className="flex-1 min-w-0"
        style={{
          gap: '6px',
          flexDirection: 'column',
          alignItems: 'flex-start',
          width: '100%',
          flex: 1,
        }}
      >
        {/* Name */}
        <BaseText
          className="truncate"
          fontSize={16}
          fontFamily="medium"
          color="label.textTextInput"
          style={{
            lineHeight: '20px',
            margin: 0,
            width: '100%',
          }}
        >
          {displayName}
        </BaseText>

        {/* Phone Number - INCREASED TEXT SIZE */}
        {subtitle && (
          <BaseText
            className="truncate"
            fontSize={16} // Increased from 14 to 16
            fontFamily="medium" // Changed from regular to medium for better visibility
            color={contact.username ? 'label.primary' : 'label.description'}
            style={{
              lineHeight: '20px', // Increased from 18px to 20px
              margin: 0,
              width: '100%',
            }}
          >
            {subtitle}
          </BaseText>
        )}
      </BaseView>

      {/* Chevron */}
      <BaseView
        style={{
          justifyContent: 'center',
          alignItems: 'flex-end',
          maxWidth: '150px',
          flexShrink: 0,
        }}
      >
        <svg
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          style={{
            color: 'rgba(255, 255, 255, 0.6)',
            flexShrink: 0,
          }}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </BaseView>
    </BaseView>
  )
}

export const RecipientSelectionScreen: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const offsets = useOffsets()
  const [isSearchFocused, setIsSearchFocused] = useState(false)

  // Get send/request data from store
  const fiatAmount = useSendStore((s) => s.fiatAmount)
  const cryptoAmount = useSendStore((s) => s.cryptoAmount)
  const type = useSendStore((s) => s.type)
  const userId = useUserStore((state) => state.userAuthenticated?.user_id)

  // Multi-select state
  const selectedRecipients = useMultipleRecipientsStore(
    (s) => s.selectedRecipients,
  )
  const toggleRecipient = useMultipleRecipientsStore((s) => s.toggleRecipient)
  const isRecipientSelected = useMultipleRecipientsStore(
    (s) => s.isRecipientSelected,
  )
  const clearSelection = useMultipleRecipientsStore((s) => s.clearSelection)
  const canAddMore = useMultipleRecipientsStore((s) => s.canAddMore)
  const maxRecipients = useMultipleRecipientsStore((s) => s.maxRecipients)

  // Local state to enable multi-select mode
  // Multi-select is only enabled for requests, not for sends
  const isRequest = type === 'request'
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(isRequest)

  // Local state for search - separate immediate and debounced like React Native
  const [searchValue, setSearchValue] = useState('')
  const [immediateSearchValue, setImmediateSearchValue] = useState('')

  // Track contacts selected from search (not in favorites) to keep showing them
  const [selectedFromSearch, setSelectedFromSearch] = useState<
    UsersAndContacts[]
  >([])

  // Feature flags
  const isEnabledTransferToNonUsers = useFeatureFlag(
    'contacts_transfer_restriction_enabled',
  )

  // Scroll to top functionality - as in React Native version
  const { showBackToTop, handleScroll, scrollToTop, scrollElementRef } =
    useScrollToTop(
      0, // No mock contacts, so no items to count
      {
        scrollThreshold: window.innerHeight / 2, // Half of screen height
        minItemsCount: 8, // As in RN version
        throttleDelay: 200, // As in RN version
      },
    )

  // Prepare recipient parameters - memoized to prevent unnecessary re-renders
  const recipientParams: RecipientParams = React.useMemo(
    () => ({
      currencyValue: cryptoAmount.amount || '0',
      currencyCode: cryptoAmount.code,
      fiatValue: fiatAmount.amount || '0',
      fiatCode: fiatAmount.code,
      type,
    }),
    [
      cryptoAmount.amount,
      cryptoAmount.code,
      fiatAmount.amount,
      fiatAmount.code,
      type,
    ],
  )

  // Handle contact selection - single-select for sends, multi-select for requests
  const handleContactSelected = useCallback(
    (contact: UsersAndContacts) => {
      // Check if user can transfer to non-users or if it's a request
      if (
        !contact.username &&
        !isEnabledTransferToNonUsers &&
        type !== 'request'
      ) {
        // For non-users when transfer is not enabled, show SMS option
        return
      }

      // For sends (single-select mode), navigate directly to confirm screen
      if (!isMultiSelectMode) {
        const fullRecipientParams: RecipientParams = {
          ...recipientParams,
          id: contact.id,
          avatar: contact.avatar_path || '',
          name:
            contact.first_name +
            (contact.last_name ? ` ${contact.last_name}` : ''),
          username: contact.username,
          phoneNumber: contact.phone_number,
          avatarBg: contact.avatarBg,
          byApp: contact.username ? 'true' : 'false',
        }
        navigate('/send-request/confirm', {
          state: fullRecipientParams,
        })
        return
      }

      // Multi-select mode (for requests)
      if (isRecipientSelected(contact.id)) {
        toggleRecipient(contact)
        // Remove from selectedFromSearch when deselected
        setSelectedFromSearch((prev) => prev.filter((c) => c.id !== contact.id))
      } else if (canAddMore()) {
        toggleRecipient(contact)
        // Add to selectedFromSearch for ALL selections (from favorites or search)
        // This ensures all selected contacts are visible in the "Selected" section
        if (!selectedFromSearch.some((c) => c.id === contact.id)) {
          setSelectedFromSearch((prev) => [...prev, contact])
        }
      }
    },
    [
      toggleRecipient,
      isRecipientSelected,
      canAddMore,
      isEnabledTransferToNonUsers,
      type,
      isMultiSelectMode,
      recipientParams,
      navigate,
      selectedRecipients.length,
      selectedFromSearch,
    ],
  )

  // Handle confirmation button press
  const handleConfirmSelection = useCallback(() => {
    if (selectedRecipients.length === 0) return

    if (selectedRecipients.length === 1) {
      // Single recipient - use existing flow
      const recipient = selectedRecipients[0]
      const fullRecipientParams: RecipientParams = {
        ...recipientParams,
        id: recipient.id,
        avatar: recipient.avatar || '',
        name: recipient.name,
        username: recipient.username,
        phoneNumber: recipient.phoneNumber,
        avatarBg: recipient.avatarBg,
        byApp: recipient.username ? 'true' : 'false',
      }

      // Clear selection and navigate to single confirm
      clearSelection()
      setIsMultiSelectMode(false)
      navigate('/send-request/confirm', {
        state: fullRecipientParams,
      })
    } else {
      // Multiple recipients - navigate to new multi-confirm screen
      navigate('/send-request/confirm-multiple', {
        state: recipientParams,
      })
    }
  }, [selectedRecipients, recipientParams, clearSelection, navigate])

  // Note: We don't clear selection on unmount because the next screen needs it
  // Selection is cleared after successful submission in the confirm/success screens

  // Restore selectedFromSearch when navigating back from confirm screen
  // This ensures previously selected contacts remain visible
  useEffect(() => {
    if (selectedRecipients.length > 0 && selectedFromSearch.length === 0) {
      // Map SelectedRecipient to UsersAndContacts format
      const mappedContacts: UsersAndContacts[] = selectedRecipients.map(
        (r) => ({
          id: r.id,
          username: r.username,
          first_name: r.name.split(' ')[0] || r.name,
          last_name: r.name.split(' ').slice(1).join(' ') || '',
          phone_number: r.phoneNumber || '',
          avatar_path: r.avatar,
          avatarBg: r.avatarBg,
        }),
      )
      setSelectedFromSearch(mappedContacts)
    }
  }, []) // Only run on mount

  const handleBackPress = () => {
    // Clear selection when going back
    if (isMultiSelectMode && selectedRecipients.length > 0) {
      clearSelection()
      setIsMultiSelectMode(false)
    }
    navigate(-1)
  }

  // Immediate change handler (like React Native onChangeText)
  const handleImmediateSearchChange = useCallback((text: string) => {
    setImmediateSearchValue(text)
  }, [])

  // Debounced change handler (like React Native onChangeTextDebounced)
  const handleSearchChange = useCallback((text: string) => {
    setSearchValue(text)
  }, [])

  const handleSearchFocus = useCallback(() => {
    setIsSearchFocused(true)
  }, [])

  const handleSearchBlur = useCallback(() => {
    setIsSearchFocused(false)
  }, [])

  const searchRef = useRef<HTMLInputElement>(null)

  // Clear handler for both states
  const handleClear = useCallback(() => {
    setImmediateSearchValue('')
    setSearchValue('')
    // Don't clear selectedFromSearch - we want to keep showing selected contacts
  }, [])

  const { data: favorites = [], isLoading: isLoadingFavorites } =
    useFavoriteUsers()

  // Add scroll handler to element on mount
  useEffect(() => {
    const scrollElement = scrollElementRef.current

    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll)

      // Also add handler to window
      const windowScrollHandler = (e: Event) => {
        handleScroll(e)
      }
      window.addEventListener('scroll', windowScrollHandler)

      return () => {
        scrollElement.removeEventListener('scroll', handleScroll)
        window.removeEventListener('scroll', windowScrollHandler)
      }
    }
  }, [handleScroll, scrollElementRef])

  return (
    <BaseSafeFormView
      style={{}}
      px={0}
      pb={0}
      flex={1}
      bgColor="background.screen"
    >
      <StickyHeaderNavigation
        title={t('translation:selectContact')}
        onBackPress={handleBackPress}
      />
      <div
        ref={scrollElementRef as React.RefObject<HTMLDivElement>}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '28px',
          padding: 0,
          paddingLeft: '13px',
          paddingRight: '13px',
          flex: 1,
          overflowY: 'auto', // Enable scrolling
          height: '100vh', // Fixed height for scrolling
        }}
      >
        {/* As in RN version gap={28} */}
        {/* Fixed Header Navigation */}
        <BaseView gap={20}>
          {' '}
          {/* As in RN version gap={20} */}
          <SearchInput
            placeholder={t('form:placeholders.search')}
            withoutBorder
            defaultValue={immediateSearchValue}
            onChangeText={handleImmediateSearchChange}
            onChangeTextDebounced={handleSearchChange}
            clean={handleClear}
            debounceDelay={300}
            onFocus={handleSearchFocus}
            onBlur={handleSearchBlur}
            ref={searchRef}
          />
        </BaseView>
        <BaseView
          flex={1}
          style={{
            paddingBottom: selectedRecipients.length > 0 ? '100px' : '0',
          }}
        >
          {/* Favorites List (Pinned) - only when not searching */}
          {/* {!immediateSearchValue && ( */}
          <FavoritesList
            onContactSelected={handleContactSelected}
            className=""
            isMultiSelectMode={isMultiSelectMode}
            isContactSelected={isRecipientSelected}
            favorites={favorites}
            isLoading={isLoadingFavorites}
            ref={searchRef}
          />
          {/* )} */}

          {/* Share Request - only for request type and when not searching and no selections */}
          {!immediateSearchValue &&
            type === 'request' &&
            selectedRecipients.length === 0 && (
              <BaseView mt={16} px={0}>
                <ShareRequest
                  fiatValue={recipientParams.fiatValue}
                  currencyValue={recipientParams.currencyValue}
                  currencyCode={recipientParams.currencyCode}
                  fiatCode={recipientParams.fiatCode as any}
                  userId={userId}
                />
              </BaseView>
            )}

          {/* Contacts Section - show during search */}
          {immediateSearchValue && (
            <BaseView mt={16}>
              <ContactsList
                search={searchValue}
                onContactSelected={handleContactSelected}
                isEnabledTransferToNonUsers={isEnabledTransferToNonUsers}
                transferType={type}
                isMultiSelectMode={isMultiSelectMode}
                isContactSelected={isRecipientSelected}
              />
            </BaseView>
          )}

          {/* Selected contacts - show when not searching */}
          {!immediateSearchValue &&
            selectedFromSearch.length > 0 &&
            !favorites.includes(selectedFromSearch[0].id) && (
              <BaseView mt={16}>
                <BaseText
                  fontSize={18}
                  fontFamily="semibold"
                  color="label.textTextInput"
                  style={{ marginBottom: '12px' }}
                >
                  {t('translation:selected', { defaultValue: 'Selected' })} (
                  {
                    selectedFromSearch.filter((contact) =>
                      isRecipientSelected(contact.id),
                    ).length
                  }
                  )
                </BaseText>
                <BaseView gap={8}>
                  {selectedFromSearch
                    .filter((contact) => isRecipientSelected(contact.id))
                    .map((contact, index) => (
                      <ContactItem
                        key={`selected-${contact.id}-${index}`}
                        contact={contact}
                        onPress={handleContactSelected}
                        isMultiSelectMode={isMultiSelectMode}
                        isSelected={isRecipientSelected(contact.id)}
                      />
                    ))}
                </BaseView>
              </BaseView>
            )}

          {/* Selection info when recipients selected */}
          {selectedRecipients.length > 9 && (
            <BaseView mt={16} px={0}>
              <BaseText
                fontSize={14}
                fontFamily="regular"
                color="label.description"
                textAlign="center"
              >
                {selectedRecipients.length} out of {maxRecipients} recipients
                selected
              </BaseText>
            </BaseView>
          )}
        </BaseView>
      </div>

      {/* Scroll to Top Button - as in React Native version */}
      <ScrollToTopButton visible={showBackToTop} onPress={scrollToTop} />

      {/* Fixed Bottom Confirm Button - only show for requests (multi-select mode) when recipients selected */}
      {isMultiSelectMode && selectedRecipients.length > 0 && (
        <BaseView
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            padding: '16px',
            paddingBottom: `${offsets.bottom + 16}px`,
            backgroundColor: '#131415',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            zIndex: 100,
          }}
        >
          <AppButton
            label={
              selectedRecipients.length === 1
                ? t('form:actions.continue')
                : t('translation:requestFrom', {
                    count: selectedRecipients.length,
                    defaultValue: `Request from ${selectedRecipients.length} recipients`,
                  })
            }
            onPress={handleConfirmSelection}
          />
        </BaseView>
      )}
    </BaseSafeFormView>
  )
}

export default RecipientSelectionScreen
